import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiFieldComponent,
  QaiLinkButtonComponent,
  QaiSearchableSelectComponent,
  QaiSearchableSelectOption,
  QaiSlotDirective,
  QaiSpinnerComponent,
  QaiTreeSelectComponent,
  SelectedOptions,
  User,
} from '@qai/ui';
import { IUserAssignments } from 'src/app/interfaces/self-serve/assignment-manager/iuser-assignments';
import {
  IParsedTeam,
  IParsedUser,
} from 'src/app/interfaces/self-serve/assignment-manager/iteams-with-users-parser';
import { IUserWithTeam } from 'src/app/interfaces/self-serve/assignment-manager/requests/iusers-assignments';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { TeamUserParserService } from '../../../services/team-user-parser.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { IUsersWithTeams } from 'src/app/interfaces/self-serve/assignment-manager/responses/iusers-with-teams';
import { ActivatedRoute } from '@angular/router';
import { AssignmentUsersConfirmationModalComponent } from '../assignment-users-confirmation-modal/assignment-users-confirmation-modal.component';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'add-users-to-assignment-modal',
  standalone: true,
  templateUrl: './add-users-to-assignment-modal.component.html',
  styleUrls: ['./add-users-to-assignment-modal.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    QaiFieldComponent,
    QaiDialogComponent,
    QaiFieldComponent,
    QaiButtonComponent,
    QaiLinkButtonComponent,
    QaiSearchableSelectComponent,
    QaiSlotDirective,
    QaiSpinnerComponent,
    QaiTreeSelectComponent,
    AssignmentUsersConfirmationModalComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AddUserToAssignmentModalComponent {
  @ViewChild('dialog') qaiDialogRef: QaiDialogComponent | undefined;
  @ViewChild(QaiTreeSelectComponent)
  treeSelectComponent!: QaiTreeSelectComponent;

  @Input() open: boolean = false;
  @Input()
  set allTeamsWithUsers(teams: IParsedTeam[]) {
    this._allTeamsWithUsers = teams;
    this.tagOptions = this.buildTagOptions(teams);
  }
  get allTeamsWithUsers(): IParsedTeam[] {
    return this._allTeamsWithUsers;
  }
  private _allTeamsWithUsers: IParsedTeam[] = [];
  @Input() networkRequestInProgress: boolean = false;
  @Input() currentAssignees: IUserAssignments[] = [];
  @Input() moduleAssignmentUuid: string = '';
  @Input() isCreateAssignmentFlow: boolean = false;

  @Output() addAssignmentClicked = new EventEmitter<string>();
  @Output() closeModal = new EventEmitter<void>();
  @Output() addToAssignmentClicked = new EventEmitter<IUserWithTeam[]>();

  selectedUsers: IUserWithTeam[] = [];
  selectedUsersWithDetails: IParsedUser[] = [];
  showReviewModal: boolean = false;
  tagOptions: QaiSearchableSelectOption[] = [];
  selectedTags: string[] = [];
  searchTerm: string = '';

  constructor(
    private assignmentsService: AssignmentsService,
    public teamUserParserService: TeamUserParserService,
    private route: ActivatedRoute,
    private translocoService: TranslocoService
  ) {}

  showError: boolean = false;
  isLoadingUsers: boolean = false;

  get dialogDescription(): string {
    return this.translocoService.translate('simulationBuilder.DIALOG_DESCRIPTION_ADD_USERS');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      const isOpenNow = changes['open']?.currentValue;

      if (isOpenNow) {
        this.openModal();
        this.networkRequestInProgress = false;
        this.getAllUsersWithTeams();
      } else {
        if (this.qaiDialogRef) {
          this.qaiDialogRef.close();
        }
      }
    }
  }

  handleSuccessClick() {
    this.networkRequestInProgress = true;
    this.addToAssignment(this.selectedUsers);
  }

  handleCloseClick() {
    this.closeModal.emit();
  }

  onCancelFromConfirmation(): void {
    this.showReviewModal = false;
    this.closeModal.emit();
  }

  onUserRemovedFromReview(userUuid: string): void {
    const user = this.selectedUsersWithDetails.find((u) => u.uuid === userUuid);
    if (user) {
      this.treeSelectComponent.removeSelected(user as User, new Event('click'));
    }
  }

  openModal() {
    this.selectedUsers = [];
    this.selectedTags = [];
    this.searchTerm = '';

    if (this.treeSelectComponent) {
      this.treeSelectComponent.deselectAll();
      this.treeSelectComponent.clearSearch();
    }

    if (this.qaiDialogRef) {
      this.qaiDialogRef.showModal();
    }
  }

  onSelectionSave(data: SelectedOptions[]) {
    const users = data as unknown as IParsedUser[];
    this.selectedUsersWithDetails = users;
    this.selectedUsers = users.map((user) => ({
      userUuid: user.uuid,
      teamUuid: user.teamUuid,
    }));
  }

  onTagsChange(value: string | string[]): void {
    if (Array.isArray(value)) {
      this.selectedTags = [...value];
    } else {
      this.selectedTags = value ? [value] : [];
    }
  }

  onSearchTermChange(term: string): void {
    this.searchTerm = term;
  }

  get hasActiveFilters(): boolean {
    return this.selectedTags.length > 0 || this.searchTerm.trim().length > 0;
  }

  clearAllFilters(): void {
    this.selectedTags = [];
    this.searchTerm = '';
    this.treeSelectComponent?.clearSearch();
  }

  private buildTagOptions(teams: IParsedTeam[]): QaiSearchableSelectOption[] {
    const tags = new Set<string>();
    const collect = (nodes: IParsedTeam[]) => {
      for (const team of nodes) {
        (team.tags ?? []).forEach((tag) => tags.add(tag));
        collect(team.children ?? []);
      }
    };
    collect(teams);

    return [...tags]
      .sort((a, b) => a.localeCompare(b))
      .map((tag) => ({ value: tag, label: tag }));
  }

  openReviewModal(): void {
    this.showReviewModal = true;
  }

  get modalHeading(): string {
    return this.isCreateAssignmentFlow
      ? this.translocoService.translate('simulationBuilder.CREATE_NEW_ASSIGNMENT_BUTTON')
      : this.translocoService.translate('simulationBuilder.ADD_USERS_TO_ASSIGNMENT_HEADING');
  }

  get modalButtonText(): string {
    return this.isCreateAssignmentFlow
      ? this.translocoService.translate('simulationBuilder.BUTTON_SAVE_AND_CLOSE')
      : this.translocoService.translate('simulationBuilder.BUTTON_ADD_TO_ASSIGNMENT');
  }

  get fieldLabel(): string {
    const baseLabel = this.translocoService.translate('simulationBuilder.FIND_USERS_LABEL');
    return this.selectedUsers.length > 0
      ? `${baseLabel} (${this.selectedUsers.length} selected)`
      : baseLabel;
  }

  onRemoveAll() {
    this.selectedUsers = [];
    if (this.treeSelectComponent) {
      this.treeSelectComponent.deselectAll();
    }
  }

  getAllUsersWithTeams(): any {
    const storedTeamsWithUsers =
      SelfServeLocalStorageUtil.getTransformedTeamsWithUsers();
    if (storedTeamsWithUsers.length !== 0) {
      this.allTeamsWithUsers =
        this.teamUserParserService.markAssignedUsersOnParsedTeams(
          storedTeamsWithUsers,
          this.currentAssignees
        );
    }

    this.isLoadingUsers = true;
    this.assignmentsService.getAllUsersWithTeams().subscribe({
      next: (usersWithTeams: IUsersWithTeams) => {
        this.allTeamsWithUsers =
          this.teamUserParserService.transformTeamUserData(usersWithTeams);

        this.allTeamsWithUsers =
          this.teamUserParserService.markAssignedUsersOnParsedTeams(
            this.allTeamsWithUsers,
            this.currentAssignees
          );

        SelfServeLocalStorageUtil.setTransformedTeamsWithUsers(
          JSON.stringify(this.allTeamsWithUsers)
        );
        this.isLoadingUsers = false;
      },
      error: (err: any) => {
        console.error('Failed to users with teams', err);
        this.isLoadingUsers = false;
      },
    });
  }

  addToAssignment(usersWithTeams: IUserWithTeam[]) {
    this.assignmentsService
      .assignUsersWithTeamContext(this.moduleAssignmentUuid, usersWithTeams)
      .subscribe({
        next: () => {
          this.showReviewModal = false;
          this.addToAssignmentClicked.emit(this.selectedUsers);
        },
        error: (err: any) => {
          console.error('Failed to users with teams', err);
        },
      });
  }
}
